// Full React component using Formik for Patient Vitals Modal
import useTitanApp from "../../../hooks/useTitanApp";
import {
  addPatientVitals,
  getPatientVitals,
  GetPatientVitalsSummaryByPatientId,
  setIncrementPaymentCompleted,
  setShowAddVitalsModal,
  updateConsultationActions,
  setIncrementVitalsCompleted,
  setVitalsDetails,
  setQuickActionsTabSwitch,
  trackServiceBilling,
  getPatientConsultationByConsultationId,
  getPatientByPatientId,
  updatePatientEthnicity,
  SaveAIChatMessage,
  setAddPatientAIChatMessage,
} from "../../../dispatchers/patientDispatchers";
import { Modal } from "react-bootstrap";
import { Button } from "@/components/ui/button";
import { IoClose } from "react-icons/io5";
import { useRef, useState, useEffect } from "react";
import { CiWavePulse1 } from "react-icons/ci";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { LuHeart } from "react-icons/lu";
import { BsThermometer } from "react-icons/bs";
import { LiaWeightHangingSolid } from "react-icons/lia";
import { ActionType } from "../../../enum/ActionType";
import CustomSelectionDropdown from "../../controls/CustomSelectionDropdown";
import { Ruler, Circle, Calculator, Wind, MoreHorizontal, AlertTriangle } from "lucide-react";
import { closeVitalsModal } from "../../../features/patientSlice/patientSlice";
import { setConsultationLoadError } from "../../../dispatchers/appDispatchers";
import removeActionFromLocal from "../../../utils/removeFromLocalStorage";
import { ethnicityTypes } from "../../../enum/ethnicityType";
import { bmiVitalLabel } from "../../../data/constantStrings";
import { getPatientActiveTreatmentPlan } from "../../../dispatchers/treatmentPlanDispatchers";
import { Gauge, AlertCircle, Droplets, Brain } from "lucide-react";
import { evaluateBmiPlausibility, evaluateWeightPlausibility } from "@/lib/vitalsData";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const ethnicityOptions = Object.entries(ethnicityTypes).map(
  ([key, label]) => ({
    value: key,
    label: label,
  }),
);

export default function PatientAddVitalsModal() {
  const { appState, patientState } = useTitanApp();
  const { darkMode, currentPharmacyId, companyId, features } = appState || {};
  const { ifNews2Enabled } = features || {};
  const {
    selectedPatient,
    selectedPatientDetails,
    showAddVitalsModal,
    addPatientVitalsLoader,
    consultationId,
    isEditMode,
    vitalToEdit,
    vitalType,
    patientVitals,
    consultationData,
    vitalMessageId,
  } = patientState || {};

  const formikRef = useRef();
  const [selectedVital, setSelectedVital] = useState(null);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [showAdditionalVitals, setShowAdditionalVitals] = useState(false);
  const [confirmCounter, setConfirmCounter] = useState(0); // just forces re-render
  const bmiConfirmedSignatureRef = useRef("");
  const weightConfirmedSignatureRef = useRef("");

  

  const createBMIVital = async (
    heightData,
    weightData,
    recordedAt,
    ethnicity,
  ) => {
    if (!selectedPatientDetails?.patientID) return;

    let heightInMeters = 0;
    let weightInKg = 0;

    if (heightData.unit === "ft/in") {
      const match = String(heightData.value).match(/(\d+)\s*ft\s*(\d*)\s*in?/i);
      const feet = match ? parseInt(match[1]) : 0;
      const inch = match && match[2] ? parseInt(match[2]) : 0;
      heightInMeters = feet * 0.3048 + inch * 0.0254;
    } else {
      heightInMeters = parseFloat(heightData.value) / 100;
    }

    if (weightData.unit === "kg") {
      weightInKg = parseFloat(weightData.value);
    } else {
      weightInKg = parseFloat(weightData.value) * 0.453592;
    }

    if (heightInMeters > 0 && weightInKg > 0) {
      const bmiValue = weightInKg / (heightInMeters * heightInMeters);

      const bmiObj = {
        patientId: String(selectedPatientDetails.patientID),
        patientName: String(
          selectedPatient || selectedPatientDetails.patientName,
        ),
        consultationId: consultationId,
        recordedAt: recordedAt,
        vitalType: "BMI Calculator",
        vitalTypeKey: "bmicalculator",
        serviceName: consultationData?.serviceName,
        bmi: {
          Value: String(bmiValue.toFixed(2)),
          Unit: "kg/m²",
          recordedAt: recordedAt,
          ethnicity: ethnicity,
        },
        height: {
          Value: heightData.value,
          Unit: heightData.unit,
          recordedAt: recordedAt,
        },
        weight: {
          Value: weightData.value,
          Unit: weightData.unit,
          recordedAt: recordedAt,
        },
        // Add recordedVitalType to help with identification
        recordedVitalType: "bmi",
      };

      try {
        // IMPORTANT: Always create NEW BMI - don't set vitalMessageId
        const bmiPayload = {
          payload: JSON.stringify({
            vitalsDetails: bmiObj,
            timestamp: recordedAt,
            actionPerformedAt: recordedAt,
          }),
          actionName: ActionType[5],
          actionNumber: "5",
          isPerformed: true,
          performedAt: recordedAt,
        };

        // Don't set vitalMessageId - we want a new action

        const response = await addPatientVitals(bmiObj);
        await getPatientActiveTreatmentPlan(selectedPatientDetails.patientID);
        if (consultationId) {
          // Just add the new action, don't try to remove old BMI
          await updateConsultationActions({
            consultationId: consultationId,
            payload: bmiPayload,
          }).then(async () => {
            await getPatientConsultationByConsultationId(consultationId);
          });

          const vitalMessage = [
            {
              sender: "success",
              message: "Vitals completed successfully.",
              actionId: 5,
              details: bmiPayload,
            },
          ];
          await SaveAIChatMessage({
            ConsultationChat: JSON.stringify(vitalMessage),
            ConsultationId: consultationId,
          });
        }

        // This will trigger a new success message in ActionsPanel
        setVitalsDetails(bmiPayload);

        if (response) setIncrementVitalsCompleted(true);

        if (consultationId) {
          try {
            await trackServiceBilling({
              companyId,
              consultationId,
              pharmacyId: currentPharmacyId,
              actiontype: "bmi calculator",
            });
          } catch (billingError) {
            console.error("Error tracking BMI billing:", billingError);
          }
        }

        return true;
      } catch (error) {
        console.error("Error creating automatic BMI vital:", error);
        return false;
      }
    }
    return false;
  };

 const closeModal = () => {
  setShowAddVitalsModal(false);
  setSelectedVital(null);
  closeVitalsModal();
  setShowAdditionalVitals(false);
  bmiConfirmedSignatureRef.current = "";
  weightConfirmedSignatureRef.current = "";
  if (formikRef.current) {
    formikRef.current.resetForm({ values: {} });
  }
};

  const vitals = [
    {
      icon: <LuHeart color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Heart Rate",
      desc: "Beats per minute (BPM)",
    },
    {
      icon: (
        <LiaWeightHangingSolid
          size={19}
          color={darkMode ? "#bdbdbd" : "#0a0a0a"}
        />
      ),
      label: "Weight",
      desc: "Body weight measurement",
    },
    {
      icon: <CiWavePulse1 size={20} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Blood Pressure",
      desc: "Systolic/Diastolic pressure",
    },
    {
      icon: (
        <BsThermometer size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />
      ),
      label: "Temperature",
      desc: "Body temperature",
    },
    {
      icon: <Wind size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Respiratory Rate",
      desc: "Body temperature",
    },
    {
      icon: <Ruler size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Height",
      desc: "Body temperature",
    },
    {
      icon: <Circle size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Circumference",
      desc: "Body temperature",
    },
    {
      icon: <Calculator size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "BMI Calculator",
      desc: "Body temperature",
    },
  ];

  const additionalVitals = [
    {
      icon: <Gauge size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Oxygen Saturation",
      desc: "SpO₂ %",
    },
    {
      icon: <AlertCircle size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "NEWS Pain Score",
      desc: "0-10 scale",
    },
    {
      icon: <Droplets size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Blood Sugar",
      desc: "Blood glucose",
    },
    {
      icon: <Brain size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Consciousness (AVPU)",
      desc: "AVPU scale",
    },
    {
      icon: <Wind size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Supplemental Oxygen",
      desc: "On supplemental O₂",
    },
  ];

  const handleVitalBoxClick = (vital) => {
  setSelectedVital(vital);
  bmiConfirmedSignatureRef.current = "";
  weightConfirmedSignatureRef.current = "";
  setConfirmCounter(0);
  if (formikRef.current) {
    formikRef.current.resetForm({ values: getInitialValues(vital.label) });
  }
};

  const getInitialValues = (label, vitalData) => {
    vitalData = vitalData || {};

    switch (label) {
      case "Heart Rate":
        return { Value: vitalData.value || vitalData?.Value || "" };
      case "Weight":
        return {
          Value: vitalData.value || vitalData.Value || "",
          Unit: vitalData.unit || vitalData.Unit || "kg",
        };
      case "Blood Pressure":
        return {
          Systolic: vitalData.systolic || vitalData?.Systolic?.Value || "",
          Diastolic: vitalData.diastolic || vitalData?.Diastolic?.Value || "",
          Unit: vitalData.unit || "mmHg",
        };
      case "Temperature":
        return {
          Value: vitalData.value || vitalData?.Value || "",
          Unit: vitalData.unit || vitalData?.Unit || "°C",
        };
      case "Respiratory Rate":
        return { Value: vitalData.value || vitalData?.Value || "" };
      case "Height":
        let val = vitalData.value || vitalData.Value;
        if ((vitalData.unit || vitalData.Unit) === "ft/in" && val) {
          const match = String(val).match(/(\d+)\s*ft\s*(\d*)\s*in?/i);
          return {
            Feet: match ? match[1] : "",
            Inch: match && match[2] ? match[2] : "",
            Unit: vitalData.unit || vitalData.Unit || "ft/in",
          };
        }
        return {
          Value: val || "",
          Unit: vitalData.unit || vitalData.Unit || "cm",
        };
      case "Circumference":
        let waist = "",
          hip = "",
          neck = "";

        let value = vitalData.value || vitalData.Value;
        if (value) {
          const waistMatch = value.match(/Waist:\s*(\d+)/i);
          const hipMatch = value.match(/Hip:\s*(\d+)/i);
          const neckMatch = value.match(/Neck:\s*(\d+)/i);
          waist = waistMatch ? waistMatch[1] : "";
          hip = hipMatch ? hipMatch[1] : "";
          neck = neckMatch ? neckMatch[1] : "";
        }
        return {
          WaistCircumference: vitalData.WaistCircumference || waist,
          HipCircumference: vitalData.HipCircumference || hip,
          NeckCircumference: vitalData.NeckCircumference || neck,
          Unit: vitalData.Unit || vitalData.unit || "cm",
        };
      case "BMI Calculator":
        // For BMI Calculator, we need to extract height and weight from the vitalData

        // Case 1: When editing an existing BMI record
        if (vitalData && (vitalData.height || vitalData.weight)) {
          const heightValue =
            vitalData.height?.Value || vitalData.height?.value || "";
          const heightUnit =
            vitalData.height?.Unit || vitalData.height?.unit || "cm";

          const weightValue =
            vitalData.weight?.Value || vitalData.weight?.value || "";
          const weightUnit =
            vitalData.weight?.Unit || vitalData.weight?.unit || "kg";

          const ethnicityValue =
            vitalData.bmi?.Ethnicity?.toString() ||
            vitalData.ethnicity?.toString() ||
            selectedPatientDetails?.ethnicity?.toString() ||
            "";

          if (heightUnit === "ft/in" && heightValue) {
            const match = String(heightValue).match(
              /(\d+)\s*ft\s*(\d*)\s*in?/i,
            );
            return {
              Feet: match ? match[1] : "",
              Inch: match && match[2] ? match[2] : "",
              HeightUnit: heightUnit,
              Weight: weightValue,
              WeightUnit: weightUnit,
              Ethnicity: ethnicityValue,
            };
          }

          return {
            Height: heightValue,
            HeightUnit: heightUnit,
            Weight: weightValue,
            WeightUnit: weightUnit,
            Ethnicity: ethnicityValue,
          };
        }

        // Case 2: When adding a new BMI (prefill from patient vitals)
        const heightValue = patientVitals?.height?.value || "";
        const heightUnit = patientVitals?.height?.unit || "cm";

        const weightValue = patientVitals?.weight?.value || "";
        const weightUnit = patientVitals?.weight?.unit || "kg";

        const ethnicityValue =
          selectedPatientDetails?.ethnicity?.toString() || "";

        if (heightUnit === "ft/in" && heightValue) {
          const match = String(heightValue).match(/(\d+)\s*ft\s*(\d*)\s*in?/i);
          return {
            Feet: match ? match[1] : "",
            Inch: match && match[2] ? match[2] : "",
            HeightUnit: heightUnit,
            Weight: weightValue,
            WeightUnit: weightUnit,
            Ethnicity: ethnicityValue,
          };
        }

        return {
          Height: heightValue,
          HeightUnit: heightUnit,
          Weight: weightValue,
          WeightUnit: weightUnit,
          Ethnicity: ethnicityValue,
        };
      case "Oxygen Saturation":
        return { Value: vitalData.Value || vitalData.value || "" };

      case "NEWS Pain Score":
        return { Value: vitalData.Value || vitalData.value || "" };

      case "Blood Sugar":
        return {
          Value: vitalData.Value || vitalData.value || "",
          Unit: vitalData.Unit || vitalData.unit || "mmol/L",
        };

      case "Consciousness (AVPU)":
        return { Value: vitalData.Value || vitalData.value || "Alert" };

      case "Supplemental Oxygen":
        return { Value: vitalData.Value || vitalData.value || "No" };
      default:
        return {};
    }
  };

  const updateEthnicityIfRequired = async (
    selectedVital,
    ethnicity,
    selectedPatientDetails,
  ) => {
    const ethnicityVal = Number(ethnicity);
    if (
      selectedVital?.label === bmiVitalLabel &&
      ethnicityVal !== 0 &&
      ethnicityVal !== selectedPatientDetails?.ethnicity
    ) {
      const ethnicityObj = {
        patientID: selectedPatientDetails?.patientID,
        ethnicity: ethnicityVal,
      };

      await updatePatientEthnicity(ethnicityObj);
    }
  };

  const handleAddClick = async (values) => {
    setButtonsDisabled(true);
    if (!selectedVital) return;

    const recordedAt = new Date().toISOString();

    let obj = {
      patientId: String(selectedPatientDetails?.patientID || ""),
      patientName: String(selectedPatient || ""),
      consultationId: consultationId,
      recordedAt: recordedAt,
      vitalType: selectedVital.label,
      vitalTypeKey: selectedVital.label.toLowerCase().replace(/\s+/g, ""),
      serviceName: consultationData?.serviceName,
    };

    let heightAdded = null;
    let weightAdded = null;
    let shouldCreateOrUpdateBMI = false;

    const parseActionTime = (action) => {
      try {
        const timeString = action.performedAt || action.actionPerformedAt;
        if (timeString) {
          return new Date(timeString).getTime();
        }
        const payload = JSON.parse(action.payload);
        if (payload.timestamp) {
          return new Date(payload.timestamp).getTime();
        }
        if (payload.vitalsDetails?.recordedAt) {
          return new Date(payload.vitalsDetails.recordedAt).getTime();
        }
      } catch (e) {}
      return new Date().getTime();
    };

    const getLatestHeightFromActions = () => {
      if (!consultationData?.actions) return null;

      let latestHeight = null;
      let latestTime = 0;

      for (const action of consultationData.actions) {
        if (action.actionName === "RecordVitals" && action.isPerformed) {
          try {
            const payload = JSON.parse(action.payload);
            const vitalType = payload?.vitalsDetails?.vitalType;
            const heightData = payload?.vitalsDetails?.height;

            if (
              (vitalType === "Height" || vitalType === "BMI Calculator") &&
              heightData
            ) {
              const actionTime = parseActionTime(action);

              if (actionTime > latestTime) {
                latestTime = actionTime;
                latestHeight = {
                  value: heightData.Value,
                  unit: heightData.Unit || heightData.unit,
                  recordedAt: heightData.recordedAt,
                };
              }
            }
          } catch (e) {
            continue;
          }
        }
      }

      return latestHeight;
    };

    const getLatestWeightFromActions = () => {
      if (!consultationData?.actions) return null;

      let latestWeight = null;
      let latestTime = 0;

      for (const action of consultationData.actions) {
        if (action.actionName === "RecordVitals" && action.isPerformed) {
          try {
            const payload = JSON.parse(action.payload);
            const vitalType = payload?.vitalsDetails?.vitalType;
            const weightData = payload?.vitalsDetails?.weight;

            if (
              (vitalType === "Weight" || vitalType === "BMI Calculator") &&
              weightData
            ) {
              const actionTime = parseActionTime(action);

              if (actionTime > latestTime) {
                latestTime = actionTime;
                latestWeight = {
                  value: weightData.Value,
                  unit: weightData.Unit || weightData.unit,
                  recordedAt: weightData.recordedAt,
                };
              }
            }
          } catch (e) {
            continue;
          }
        }
      }

      return latestWeight;
    };

    const hasHeightInActions = () => {
      return getLatestHeightFromActions() !== null;
    };

    const hasWeightInActions = () => {
      return getLatestWeightFromActions() !== null;
    };

    if (selectedVital.label === "Heart Rate") {
      obj.heartRate = {
        Value: String(values.Value || ""),
        Unit: "BPM",
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "Weight") {
      weightAdded = {
        value: String(values.Value || ""),
        unit: String(values.Unit || ""),
      };
      obj.weight = {
        Value: weightAdded.value,
        Unit: weightAdded.unit,
        recordedAt: recordedAt,
      };

      // BMI should trigger in BOTH add and edit modes
      // if (hasHeightInActions()) {
      //   shouldCreateOrUpdateBMI = true;
      // }
      const heightExists =
        hasHeightInActions() || !!patientVitals?.height?.value;
      if (heightExists) {
        shouldCreateOrUpdateBMI = true;
      }
    } else if (selectedVital.label === "Blood Pressure") {
      obj.bloodPressure = {
        Systolic: {
          Value: String(values.Systolic || ""),
          Unit: "mmHg",
          recordedAt: recordedAt,
        },
        Diastolic: {
          Value: String(values.Diastolic || ""),
          Unit: "mmHg",
          recordedAt: recordedAt,
        },
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "Temperature") {
      obj.temperature = {
        Value: String(values.Value || ""),
        Unit: String(values.Unit || ""),
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "Respiratory Rate") {
      obj.respiratoryRate = {
        Value: String(values.Value || ""),
        Unit: "breaths/min",
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "Height") {
      let heightString = "";
      if (values.Unit === "cm") {
        heightString = String(values.Value || "");
      } else if (values.Unit === "ft/in") {
        const feet = parseInt(values.Feet) || 0;
        const inch =
          values.Inch === "" || values.Inch == null ? 0 : parseInt(values.Inch);
        heightString = `${feet} ft ${inch} in`;
      }

      heightAdded = {
        value: heightString,
        unit: String(values.Unit || ""),
      };
      obj.height = {
        Value: heightAdded.value,
        Unit: heightAdded.unit,
        recordedAt: recordedAt,
      };

      // BMI should trigger in BOTH add and edit modes
      const weightExists =
        hasWeightInActions() || !!patientVitals?.weight?.value;
      if (weightExists) {
        shouldCreateOrUpdateBMI = true;
      }
    } else if (selectedVital.label === "Circumference") {
      obj.circumferenceValue = {
        Unit: String(values.Unit || ""),
        Waist: String(values.WaistCircumference || ""),
        Hip: String(values.HipCircumference || ""),
        Neck: String(values.NeckCircumference || ""),
      };
      obj.circumference = {
        Unit: String(values.Unit || ""),
        Value: [
          values.WaistCircumference
            ? `Waist: ${values.WaistCircumference}`
            : null,
          values.HipCircumference ? `Hip: ${values.HipCircumference}` : null,
          values.NeckCircumference ? `Neck: ${values.NeckCircumference}` : null,
        ]
          .filter(Boolean)
          .join(" "),
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "BMI Calculator") {
      let heightString = "";
      let heightInMeters = 0;

      if (values.HeightUnit === "cm") {
        heightString = String(values.Height || "");
        heightInMeters = parseFloat(values.Height) / 100;
      } else if (values.HeightUnit === "ft/in") {
        const feet = parseInt(values.Feet) || 0;
        const inch =
          values.Inch === "" || values.Inch == null ? 0 : parseInt(values.Inch);
        heightString = `${feet} ft ${inch} in`;
        heightInMeters = feet * 0.3048 + inch * 0.0254;
      }
      const ethnicityValue = values.Ethnicity
        ? parseInt(values.Ethnicity)
        : selectedPatientDetails?.ethnicity || 0;
      const weightInKg =
        values.WeightUnit === "kg"
          ? parseFloat(values.Weight)
          : parseFloat(values.Weight) * 0.453592;

      obj.height = {
        Value: String(heightString || ""),
        Unit: String(values.HeightUnit || ""),
        recordedAt: recordedAt,
      };
      obj.weight = {
        Value: String(values.Weight || ""),
        Unit: String(values.WeightUnit || ""),
        recordedAt: recordedAt,
      };
      const bmi = weightInKg / (heightInMeters * heightInMeters);
      if (bmi) {
        obj.bmi = {
          Value: String(bmi.toFixed(2)),
          Unit: "kg/m²",
          recordedAt: recordedAt,
          Ethnicity: ethnicityValue,
        };
      }
    } else if (selectedVital.label === "Oxygen Saturation") {
      obj.OxygenSaturation = {
        Value: String(values.Value || ""),
        Unit: "%",
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "NEWS Pain Score") {
      obj.PainScore = {
        Value:
          values.Value !== undefined &&
          values.Value !== null &&
          values.Value !== ""
            ? String(values.Value)
            : "",
        Unit: "/10",
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "Blood Sugar") {
      obj.GlucoseLevel = {
        Value: String(values.Value || ""),
        Unit: String(values.Unit || "mmol/L"),
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "Consciousness (AVPU)") {
      obj.Consciousness = {
        Value: String(values.Value || "Alert"),
        Unit: "",
        recordedAt: recordedAt,
      };
    } else if (selectedVital.label === "Supplemental Oxygen") {
      obj.SupplementalOxygen = {
        Value: String(values.Value || "No"),
        Unit: "",
        recordedAt: recordedAt,
      };
    }

    // IMPORTANT: When in edit mode, we DON'T want to use the old vitalMessageId
    // We want a NEW action, so don't set vitalMessageId
    const vitalsPayload = {
      payload: JSON.stringify({
        vitalsDetails: obj,
        timestamp: recordedAt,
        actionPerformedAt: recordedAt,
      }),
      actionName: ActionType[5],
      actionNumber: "5",
      isPerformed: true,
      performedAt: recordedAt,
    };
    // DO NOT set vitalMessageId in edit mode - this ensures it's treated as a NEW action
    // Removed all vitalMessageId assignment code

    try {
      const response = await addPatientVitals(obj);
      await getPatientActiveTreatmentPlan(selectedPatientDetails.patientID);
      await updateEthnicityIfRequired(
        selectedVital,
        values.Ethnicity,
        selectedPatientDetails,
      );

      if (consultationId) {
        closeModal();

        // When in edit mode, we DON'T want to remove the old action
        // We want to KEEP the old action and ADD a new one
        // So we removed the code that finds and removes the old action

        // Just add the NEW action without removing the old one
        await updateConsultationActions({
          consultationId: consultationId,
          payload: vitalsPayload,
        }).then(async () => {
          await getPatientConsultationByConsultationId(consultationId);
        });
        const vitalMessage = [
          {
            sender: "success",
            message: "Vitals completed successfully.",
            actionId: 5,
            details: vitalsPayload,
          },
        ];
        await SaveAIChatMessage({
          ConsultationChat: JSON.stringify(vitalMessage),
          ConsultationId: consultationId,
        });
        // setAddPatientAIChatMessage({
        //   sender: "success",
        //   message: "Vitals completed successfully.",
        //   actionId: 5,
        //   details: vitalsPayload,
        // });
      }

      setVitalsDetails(vitalsPayload);
      removeActionFromLocal(consultationId, "5");
      setQuickActionsTabSwitch(0);

      if (response) setIncrementVitalsCompleted(true);

      try {
        if (consultationId) {
          await trackServiceBilling({
            companyId,
            consultationId,
            pharmacyId: currentPharmacyId,
            actiontype: selectedVital.label?.toLowerCase() || "vital",
          });
        }
      } catch (err) {
        console.error("Error calling billing tracker for vitals:", err);
      }
      if (shouldCreateOrUpdateBMI) {
        if (selectedVital.label === "Weight") {
          // ✅ Fall back to patientVitals if no height found in actions
          const latestHeight =
            getLatestHeightFromActions() ||
            (patientVitals?.height?.value
              ? {
                  value: patientVitals.height.value,
                  unit: patientVitals.height.unit,
                }
              : null);

          if (latestHeight && weightAdded) {
            const ethnicityValue = values.Ethnicity
              ? parseInt(values.Ethnicity)
              : latestHeight.ethnicity || 0;
            await createBMIVital(
              latestHeight,
              weightAdded,
              recordedAt,
              ethnicityValue,
            );
          }
        } else if (selectedVital.label === "Height") {
          // ✅ Fall back to patientVitals if no weight found in actions
          const latestWeight =
            getLatestWeightFromActions() ||
            (patientVitals?.weight?.value
              ? {
                  value: patientVitals.weight.value,
                  unit: patientVitals.weight.unit,
                }
              : null);

          if (latestWeight && heightAdded) {
            const ethnicityValue = values.Ethnicity
              ? parseInt(values.Ethnicity)
              : latestWeight.ethnicity || 0;
            await createBMIVital(
              heightAdded,
              latestWeight,
              recordedAt,
              ethnicityValue,
            );
          }
        }
      }

      await getPatientVitals(selectedPatientDetails?.patientID);
      await GetPatientVitalsSummaryByPatientId(
        selectedPatientDetails?.patientID,
      );
    } catch (error) {
      console.error("Error in handleAddClick:", error);
      setConsultationLoadError(true);
    } finally {
      setButtonsDisabled(false);
    }
  };

  // 2. Add BMI calculation function
  const calculateBMI = (height, heightUnit, weight, weightUnit, feet, inch) => {
    if (!heightUnit || !weightUnit || !weight) return null;
    let heightInMeters = 0;
    if (heightUnit === "cm") {
      const heightNum = parseFloat(height);
      if (!heightNum || heightNum <= 0) return null;
      heightInMeters = heightNum / 100;
    } else if (heightUnit === "ft/in") {
      const feetNum = parseInt(feet);
      const inchNum = parseInt(inch);
      heightInMeters = feetNum * 0.3048 + inchNum * 0.0254;
      if (heightInMeters <= 0) return null;
    } else if (heightUnit === "ft") {
      const heightNum = parseFloat(height);
      if (!heightNum || heightNum <= 0) return null;
      heightInMeters = heightNum * 0.3048;
    }
    const weightNum = parseFloat(weight);
    if (!weightNum || heightInMeters <= 0) return null;
    const weightInKg = weightUnit === "kg" ? weightNum : weightNum * 0.453592;
    const bmi = weightInKg / (heightInMeters * heightInMeters);
    return bmi.toFixed(2);
  };

  // 3. Get BMI category function
  const getBMICategory = (bmi, ethnicity) => {
    const thresholds = getBMIThresholds(ethnicity);
    if (!bmi) return "";
    if (bmi < thresholds.underweight) return "Underweight";
    if (bmi < thresholds.normal) return "Normal weight";
    if (bmi < thresholds.overweight) return "Overweight";
    return "Obese";
  };

  const getBMIThresholds = (ethnicity) => {
    const whiteThresholds = {
      underweight: 18.5,
      normal: 25,
      overweight: 30,
    };

    const nonWhiteThresholds = {
      underweight: 18.5,
      normal: 23,
      overweight: 27.5,
    };

    // If no ethnicity selected, use standard WHO thresholds
    if (!ethnicity || ethnicity === "" || ethnicity === "0") {
      return whiteThresholds;
    }

    // Get the ethnicity key and label
    const ethnicityKey = String(ethnicity);
    const ethnicityLabel = ethnicity ? ethnicityTypes[ethnicity] : null;
    // Check if it's a White ethnicity
    // This checks both the key and the label
    const isWhiteEthnicity =
      ethnicityKey.toLowerCase().includes("white") ||
      ethnicityLabel.toLowerCase().includes("white");

    if (isWhiteEthnicity) {
      return whiteThresholds;
    }

    // For all other ethnicities, use non-White thresholds
    return nonWhiteThresholds;
  };

  const getBMICategoryClass = (category) => {
    switch (category) {
      case "Normal weight":
        return "normal";
      case "Underweight":
        return "underweight";
      case "Overweight":
        return "overweight";
      case "Obese":
        return "obese";
      default:
        return "default";
    }
  };

  useEffect(() => {
    if (isEditMode && vitalType) {
      let mappedType = vitalType;
      if (vitalType === "BMI" || vitalType === "Bmi")
        mappedType = "BMI Calculator";
      if (vitalType === "HeartRate") mappedType = "Heart Rate";
      if (vitalType === "RespiratoryRate") mappedType = "Respiratory Rate";
      // New vitals come through already formatted from formatVitalLabel
      // but map just in case
      if (vitalType === "OxygenSaturation") mappedType = "Oxygen Saturation";
      if (vitalType === "PainScore") mappedType = "NEWS Pain Score";
      if (vitalType === "GlucoseLevel") mappedType = "Blood Sugar";
      if (vitalType === "Consciousness") mappedType = "Consciousness (AVPU)";
      if (vitalType === "SupplementalOxygen")
        mappedType = "Supplemental Oxygen";

      const allVitalOptions = [...vitals, ...additionalVitals];
      const foundVital = allVitalOptions.find((v) => v.label === mappedType);
      setSelectedVital(foundVital || null);
    }
  }, [isEditMode, vitalType]);

  return (
    <Modal
      show={showAddVitalsModal}
      onHide={closeModal}
      centered
      backdrop="static"
      dialogClassName="pc-modal add-vitals"
      size="md"
    >
      <div className="header">
        <div
          className="info pc-add-modal-header-info"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: 1,
          }}
        >
          {showAdditionalVitals && !selectedVital && (
            <button
              onClick={() => setShowAdditionalVitals(false)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                border: `1px solid ${
                  darkMode ? "rgba(189, 189, 189, 0.2)" : "rgba(0, 0, 0, 0.1)"
                }`,
                background: darkMode
                  ? "rgba(189, 189, 189, 0.05)"
                  : "rgba(0, 0, 0, 0.02)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                padding: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = darkMode
                  ? "rgba(189, 189, 189, 0.15)"
                  : "rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.borderColor = darkMode
                  ? "rgba(189, 189, 189, 0.4)"
                  : "rgba(0, 0, 0, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = darkMode
                  ? "rgba(189, 189, 189, 0.05)"
                  : "rgba(0, 0, 0, 0.02)";
                e.currentTarget.style.borderColor = darkMode
                  ? "rgba(189, 189, 189, 0.2)"
                  : "rgba(0, 0, 0, 0.1)";
              }}
            >
              <span
                style={{
                  fontSize: "18px",
                  color: darkMode ? "#bdbdbd" : "#666",
                  fontWeight: "600",
                }}
              >
                ←
              </span>
            </button>
          )}
          <p className="label">
            {selectedVital ? (
              <>
                {selectedVital.icon} &nbsp;
                {isEditMode ? "Edit" : "Add"} {selectedVital.label}
              </>
            ) : showAdditionalVitals ? (
              "Additional Vitals"
            ) : (
              "Select Vital Type"
            )}
          </p>
        </div>
        <IoClose
          size={20}
          color={darkMode ? "#bdbdbd" : "#7c7c7c"}
          onClick={closeModal}
          style={{ cursor: "pointer" }}
        />
      </div>

      <div className="body" id="bodyID">
        {!selectedVital ? (
          <div className="vital-boxes-wrapper">
            <div className="vital-grid">
              {(showAdditionalVitals
                ? additionalVitals
                : [
                    ...vitals,
                    ...(ifNews2Enabled
                      ? [
                          {
                            icon: (
                              <MoreHorizontal
                                size={18}
                                color={darkMode ? "#bdbdbd" : "#0a0a0a"}
                              />
                            ), // placeholder, overridden below
                            label: "__other__",
                            desc: "More vital types",
                            isOther: true,
                          },
                        ]
                      : []),
                  ]
              ).map((vital, index) => {
                if (vital.isOther) {
                  return (
                    <div
                      key="other"
                      className="vital-box"
                      onClick={() => setShowAdditionalVitals(true)}
                    >
                      <MoreHorizontal
                        size={18}
                        color={darkMode ? "#bdbdbd" : "#0a0a0a"}
                      />
                      <p className="label">Other</p>
                    </div>
                  );
                }
                return (
                  <div
                    key={index}
                    className="vital-box"
                    onClick={() => handleVitalBoxClick(vital)}
                  >
                    {vital.icon}
                    <p className="label">{vital.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <Formik
            key={selectedVital?.label ?? "none"}
            innerRef={formikRef}
            enableReinitialize
            initialValues={getInitialValues(selectedVital?.label, vitalToEdit)}
            validationSchema={
              selectedVital?.label === "Blood Pressure"
                ? Yup.object({
                    Systolic: Yup.number().required(
                      "Systolic Pressure is required",
                    ),
                    Diastolic: Yup.number().required(
                      "Diastolic Pressure is required",
                    ),
                  })
                : selectedVital?.label === "Heart Rate" ||
                    selectedVital?.label === "Respiratory Rate"
                  ? Yup.object({
                      Value: Yup.number().required(
                        `${selectedVital?.label} is required`,
                      ),
                    })
                  : selectedVital?.label === "Circumference"
                    ? Yup.object({
                        Unit: Yup.string().required("Unit is required"),
                        WaistCircumference: Yup.number()
                          .nullable()
                          .transform((value, originalValue) =>
                            String(originalValue).trim() === "" ? null : value,
                          ),
                        HipCircumference: Yup.number()
                          .nullable()
                          .transform((value, originalValue) =>
                            String(originalValue).trim() === "" ? null : value,
                          ),
                        NeckCircumference: Yup.number()
                          .nullable()
                          .transform((value, originalValue) =>
                            String(originalValue).trim() === "" ? null : value,
                          ),
                      }).test(
                        "at-least-one-circumference",
                        null,
                        function (value) {
                          const {
                            WaistCircumference,
                            HipCircumference,
                            NeckCircumference,
                          } = value || {};
                          if (
                            WaistCircumference == null &&
                            HipCircumference == null &&
                            NeckCircumference == null
                          ) {
                            return this.createError({
                              path: "atLeastOneCircumference",
                              message:
                                "At least one of Waist, Hip, or Neck Circumference is required",
                            });
                          }
                          return true;
                        },
                      )
                    : selectedVital?.label === "BMI Calculator"
                      ? Yup.object({
                          Feet: Yup.number().when("HeightUnit", {
                            is: "ft/in",
                            then: (schema) =>
                              schema
                                .required("Feet is required")
                                .min(0, "Feet must be greater than 0")
                                .max(11, "Feet must be less than 11"),
                            otherwise: (schema) => schema.notRequired(),
                          }),
                          Inch: Yup.number().when("HeightUnit", {
                            is: "ft/in",
                            then: (schema) =>
                              schema
                                .min(0, "Inch must be greater than 0")
                                .max(11, "Inch must be less than 12"),
                            otherwise: (schema) => schema.notRequired(),
                          }),
                          Height: Yup.number().when("HeightUnit", {
                            is: "cm",
                            then: (schema) =>
                              schema.required("Height is required"),
                            otherwise: (schema) => schema.notRequired(),
                          }),
                          HeightUnit: Yup.string().required(
                            "Height Unit is required",
                          ),
                          Weight: Yup.number().required("Weight is required"),
                          WeightUnit: Yup.string().required(
                            "Weight Unit is required",
                          ),
                          Ethnicity: Yup.string().optional(),
                        })
                      : selectedVital?.label === "Height"
                        ? Yup.object({
                            Unit: Yup.string().required("Unit is required"),
                            Feet: Yup.number().when("Unit", {
                              is: "ft/in",
                              then: (schema) =>
                                schema
                                  .required("Feet is required")
                                  .min(0)
                                  .max(11),
                              otherwise: (schema) => schema.notRequired(),
                            }),
                            Inch: Yup.number().when("Unit", {
                              is: "ft/in",
                              then: (schema) => schema.min(0).max(11),
                              otherwise: (schema) => schema.notRequired(),
                            }),
                            Value: Yup.number().when("Unit", {
                              is: "cm",
                              then: (schema) =>
                                schema.required("Height is required"),
                              otherwise: (schema) => schema.notRequired(),
                            }),
                          })
                        : selectedVital?.label === "Oxygen Saturation" ||
                            selectedVital?.label === "NEWS Pain Score"
                          ? Yup.object({
                              Value: Yup.number().required(
                                `${selectedVital?.label} is required`,
                              ),
                            })
                          : selectedVital?.label === "Blood Sugar"
                            ? Yup.object({
                                Value: Yup.number().required(
                                  "Blood sugar is required",
                                ),
                                Unit: Yup.string().required("Unit is required"),
                              })
                            : selectedVital?.label === "Consciousness (AVPU)" ||
                                selectedVital?.label === "Supplemental Oxygen"
                              ? Yup.object({
                                  Value: Yup.string().required(
                                    "This field is required",
                                  ),
                                })
                              : Yup.object({
                                  Value: Yup.number().required(
                                    `${selectedVital?.label} is required`,
                                  ),
                                  Unit: Yup.string().required(
                                    "Unit is required",
                                  ),
                                })
            }
            onSubmit={(values) => handleAddClick(values)}
>
  {({ values, errors, touched, handleChange, setFieldValue }) => {
    // --- Weight plausibility ---
    const weightRawValue =
      selectedVital?.label === "Weight" && values.Value !== "" && values.Value != null
        ? parseFloat(values.Value)
        : null;
    const weightPlausibility =
      weightRawValue !== null && !isNaN(weightRawValue)
        ? evaluateWeightPlausibility(weightRawValue, values.Unit)
        : null;
    const weightInputSignature = `${values.Value}|${values.Unit}`;
    const isWeightConfirmedForCurrent =
      weightConfirmedSignatureRef.current === weightInputSignature &&
      weightInputSignature !== "|"; // guard against empty initial state
  
    const weightBlocksSave =
      selectedVital?.label === "Weight" &&
      weightPlausibility !== null &&
      (weightPlausibility.tier === "hard_reject" ||
        (weightPlausibility.tier === "confirmation_prompt" && !isWeightConfirmedForCurrent));
    
        const handleConfirmWeightPlausibility = () => {
        weightConfirmedSignatureRef.current = weightInputSignature;
        setConfirmCounter(c => c + 1); // trigger re-render so isWeightConfirmedForCurrent updates
        };

    // --- BMI plausibility ---
    const bmiRawString =
      selectedVital?.label === "BMI Calculator"
        ? calculateBMI(values.Height, values.HeightUnit, values.Weight, values.WeightUnit, values.Feet, values.Inch)
        : null;
    const bmiRawValue = bmiRawString ? parseFloat(bmiRawString) : null;
    const bmiPlausibility =
      bmiRawValue !== null && !isNaN(bmiRawValue) ? evaluateBmiPlausibility(bmiRawValue) : null;
    // const bmiInputSignature = `${values.Height}|${values.HeightUnit}|${values.Feet}|${values.Inch}|${values.Weight}|${values.WeightUnit}`;
    // const isBmiConfirmedForCurrent =
    //   bmiConfirmed && bmiConfirmedSignatureRef.current === bmiInputSignature;
    const bmiInputSignature = `${values.Height}|${values.HeightUnit}|${values.Feet}|${values.Inch}|${values.Weight}|${values.WeightUnit}`;
    const isBmiConfirmedForCurrent =
      bmiConfirmedSignatureRef.current === bmiInputSignature &&
      bmiInputSignature !== "||||";
    const bmiBlocksSave =
      selectedVital?.label === "BMI Calculator" &&
      bmiPlausibility !== null &&
      (bmiPlausibility.tier === "hard_reject" ||
        (bmiPlausibility.tier === "confirmation_prompt" && !isBmiConfirmedForCurrent));
    const handleConfirmBmiPlausibility = () => {
      bmiConfirmedSignatureRef.current = bmiInputSignature;
      setConfirmCounter(c => c + 1);
    };

    return (
              <Form>
                {selectedVital?.label === "Heart Rate" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <label>Heart Rate (BPM) *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="Value"
                        placeholder="e.g., 72"
                        value={values.Value}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.Value && errors.Value && (
                        <p className="field-error">{errors.Value}</p>
                      )}
                    </div>
                  </div>
                )}

                {selectedVital?.label === "Weight" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <label>Weight *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="Value"
                        placeholder="e.g., 70.5"
                        value={values.Value}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.Value && errors.Value && (
                        <p className="field-error">{errors.Value}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <CustomSelectionDropdown
                        label="Unit *"
                        name="Unit"
                        value={values.Unit}
                        options={[
                          { value: "kg", label: "kg" },
                          { value: "lbs", label: "lbs" },
                        ]}
                        onChange={(val) =>
                          setFieldValue("Unit", val.target.value)
                        }
                      />
                      {touched.Unit && errors.Unit && (
                        <p className="field-error">{errors.Unit}</p>
                      )}
                    </div>

                      <div>
                      {weightPlausibility?.tier === "hard_reject" && (
                         <Alert className="border-red-200 bg-red-50 text-red-900">
                                        <AlertCircle className="h-4 w-4 text-red-600" />
                                        <AlertDescription>{weightPlausibility.message}</AlertDescription>
                                      </Alert>
                    )}

                    {weightPlausibility?.tier === "confirmation_prompt" && !isWeightConfirmedForCurrent && (
                       <div className="border rounded-lg p-3 border-amber-200 bg-amber-50">
                                      <div className="flex items-start gap-2 mb-2">
                                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                                        <p className="text-sm text-amber-900">{weightPlausibility.message}</p>
                                      </div>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={handleConfirmWeightPlausibility}
                                        className="border-amber-400 text-amber-900 hover:bg-amber-100"
                                      >
                                        Confirm value is correct
                                      </Button>
                                    </div>
                    )}

                    {weightPlausibility?.tier === "confirmation_prompt" && isWeightConfirmedForCurrent && (
                       <div className="text-xs px-3 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800">
                        Unusual value confirmed
                      </div>
                    )}
                  </div>
                  </div>
                )}

                {selectedVital?.label === "Blood Pressure" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <label>Systolic Pressure (mmHg) *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="Systolic"
                        placeholder="e.g., 120"
                        value={values.Systolic}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.Systolic && errors.Systolic && (
                        <p className="field-error">{errors.Systolic}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Diastolic Pressure (mmHg) *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="Diastolic"
                        placeholder="e.g., 80"
                        value={values.Diastolic}
                        onChange={handleChange}
                      />
                      {touched.Diastolic && errors.Diastolic && (
                        <p className="field-error">{errors.Diastolic}</p>
                      )}
                    </div>
                  </div>
                )}

                {selectedVital?.label === "Temperature" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <label>Temperature *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="Value"
                        placeholder="e.g., 37"
                        value={values.Value}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.Value && errors.Value && (
                        <p className="field-error">{errors.Value}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <CustomSelectionDropdown
                        label="Unit *"
                        name="Unit"
                        value={values.Unit}
                        options={[
                          { value: "°C", label: "°C" },
                          { value: "°F", label: "°F" },
                        ]}
                        onChange={(val) =>
                          setFieldValue("Unit", val.target.value)
                        }
                      />
                      {touched.Unit && errors.Unit && (
                        <p className="field-error">{errors.Unit}</p>
                      )}
                    </div>
                  </div>
                )}

                {selectedVital?.label === "Respiratory Rate" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <label>Respiratory Rate (breaths/min) *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="Value"
                        placeholder="e.g., 16"
                        value={values.Value}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.Value && errors.Value && (
                        <p className="field-error">{errors.Value}</p>
                      )}
                    </div>
                  </div>
                )}

                {selectedVital?.label === "Height" && (
                  <div className="fields-wrapper">
                    {values.Unit === "ft/in" ? (
                      <>
                        <div className="form-group">
                          <label>Feet *</label>
                          <input
                            className="template_input text_input_field"
                            type="number"
                            name="Feet"
                            placeholder="e.g., 5"
                            value={values.Feet || ""}
                            onChange={handleChange}
                            autoFocus
                            min={0}
                          />
                          {touched.Feet && errors.Feet && (
                            <p className="field-error">{errors.Feet}</p>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Inch *</label>
                          <input
                            className="template_input text_input_field"
                            type="number"
                            name="Inch"
                            placeholder="e.g., 6"
                            value={values.Inch || ""}
                            onChange={handleChange}
                            min={0}
                            max={11}
                          />
                          {touched.Inch && errors.Inch && (
                            <p className="field-error">{errors.Inch}</p>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group">
                          <label>Height *</label>
                          <input
                            className="template_input text_input_field"
                            type="number"
                            name="Value"
                            placeholder="e.g., 170"
                            value={values.Value}
                            onChange={handleChange}
                            autoFocus
                          />
                          {touched.Value && errors.Value && (
                            <p className="field-error">{errors.Value}</p>
                          )}
                        </div>
                      </>
                    )}

                    <div className="form-group">
                      <CustomSelectionDropdown
                        label="Unit *"
                        name="Unit"
                        value={values.Unit}
                        options={[
                          { value: "cm", label: "cm" },
                          { value: "ft/in", label: "ft/in" },
                        ]}
                        onChange={(val) => {
                          setFieldValue("Unit", val.target.value);
                          setFieldValue("Value", "");
                          setFieldValue("Feet", "");
                          setFieldValue("Inch", "");
                        }}
                      />
                      {touched.Unit && errors.Unit && (
                        <p className="field-error">{errors.Unit}</p>
                      )}
                    </div>
                  </div>
                )}

                {selectedVital?.label === "Circumference" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <CustomSelectionDropdown
                        label="Unit *"
                        name="Unit"
                        value={values.Unit}
                        options={[
                          { value: "cm", label: "cm" },
                          { value: "in", label: "in" },
                        ]}
                        onChange={(val) =>
                          setFieldValue("Unit", val.target.value)
                        }
                      />
                      {touched.Unit && errors.Unit && (
                        <p className="field-error">{errors.Unit}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Waist Circumference *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="WaistCircumference"
                        placeholder="e.g., 80"
                        value={values.WaistCircumference}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.WaistCircumference &&
                        errors.WaistCircumference && (
                          <p className="field-error">
                            {errors.WaistCircumference}
                          </p>
                        )}
                    </div>
                    <div className="form-group">
                      <label>Hip Circumference *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="HipCircumference"
                        placeholder="e.g., 90"
                        value={values.HipCircumference}
                        onChange={handleChange}
                      />
                      {touched.HipCircumference && errors.HipCircumference && (
                        <p className="field-error">{errors.HipCircumference}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Neck Circumference *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="NeckCircumference"
                        placeholder="e.g., 35"
                        value={values.NeckCircumference}
                        onChange={handleChange}
                      />
                      {touched.NeckCircumference &&
                        errors.NeckCircumference && (
                          <p className="field-error">
                            {errors.NeckCircumference}
                          </p>
                        )}
                      {errors?.atLeastOneCircumference && (
                        <p className="field-error">
                          {errors.atLeastOneCircumference}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {selectedVital?.label === "BMI Calculator" && (
                  <div className="fields-wrapper BMICalculator-fields-wrapper">
                    <div className="form-row">
                      {values.HeightUnit === "ft/in" ? (
                        <>
                          <div className="form-group">
                            <label>Feet *</label>
                            <input
                              className="template_input text_input_field"
                              type="number"
                              name="Feet"
                              placeholder="e.g., 5"
                              value={values.Feet || ""}
                              onChange={handleChange}
                              autoFocus
                              min={0}
                            />
                            {touched.Feet && errors.Feet && (
                              <p className="field-error">{errors.Feet}</p>
                            )}
                          </div>
                          <div className="form-group">
                            <label>Inch *</label>
                            <input
                              className="template_input text_input_field"
                              type="number"
                              name="Inch"
                              placeholder="e.g., 6"
                              value={values.Inch || ""}
                              onChange={handleChange}
                              min={0}
                              max={11}
                            />
                            {touched.Inch && errors.Inch && (
                              <p className="field-error">{errors.Inch}</p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="form-group">
                            <label>Height *</label>
                            <input
                              className="template_input text_input_field"
                              type="number"
                              name="Height"
                              placeholder="e.g., 170"
                              value={values.Height}
                              onChange={handleChange}
                              autoFocus
                            />
                            {touched.Height && errors.Height && (
                              <p className="field-error">{errors.Height}</p>
                            )}
                          </div>
                        </>
                      )}
                      <div className="form-group">
                        <CustomSelectionDropdown
                          label="Unit *"
                          name="HeightUnit"
                          value={values.HeightUnit}
                          options={[
                            { value: "cm", label: "cm" },
                            { value: "ft/in", label: "ft/in" },
                          ]}
                          onChange={(val) => {
                            setFieldValue("HeightUnit", val.target.value);
                            setFieldValue("Height", "");
                            setFieldValue("Feet", "");
                            setFieldValue("Inch", "");
                          }}
                        />
                        {touched.Unit && errors.Unit && (
                          <p className="field-error">{errors.Unit}</p>
                        )}
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Weight *</label>
                        <input
                          className="template_input text_input_field"
                          type="number"
                          name="Weight"
                          placeholder="e.g., 70"
                          value={values.Weight}
                          onChange={handleChange}
                        />
                        {touched.Weight && errors.Weight && (
                          <p className="field-error">{errors.Weight}</p>
                        )}
                      </div>
                      <div className="form-group">
                        <CustomSelectionDropdown
                          label="Weight Unit *"
                          name="WeightUnit"
                          value={values.WeightUnit}
                          options={[
                            { value: "kg", label: "kg" },
                            { value: "lbs", label: "lbs" },
                          ]}
                          onChange={(val) =>
                            setFieldValue("WeightUnit", val.target.value)
                          }
                        />
                        {touched.WeightUnit && errors.WeightUnit && (
                          <p className="field-error">{errors.WeightUnit}</p>
                        )}
                      </div>
                    </div>

                    {/* Ethnicity Selection */}
                    <div className="form-group">
                      <CustomSelectionDropdown
                        label="Ethnicity"
                        name="Ethnicity"
                        value={values.Ethnicity || ""}
                        options={[
                          { value: "", label: "Select ethnicity" },
                          ...ethnicityOptions,
                        ]}
                        onChange={(val) =>
                          setFieldValue("Ethnicity", val.target.value)
                        }
                        placeholder="Select ethnicity"
                      />
                      <p
                        className={`text-xs ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        BMI thresholds are adjusted based on ethnicity (NICE
                        guidelines)
                      </p>
                    </div>

                    {/* BMI Result Display */}
                    {(() => {
                      const bmi = calculateBMI(
                        values.Height,
                        values.HeightUnit,
                        values.Weight,
                        values.WeightUnit,
                        values.Feet,
                        values.Inch,
                      );
                      const category = getBMICategory(bmi, values.Ethnicity);
                      const categoryClass = getBMICategoryClass(category);

                      // Get ethnicity label if selected
                      const ethnicityLabel = values.Ethnicity
                        ? ethnicityTypes[values.Ethnicity]
                        : null;

                      return bmi ? (
                        <>
                        <div
                          className={`bmi-result bmi-result--${categoryClass}`}
                        >
                          <div className="bmi-result-header">
                            <div className="bmi-title-wrapper">
                              <Calculator
                                size={18}
                                color={
                                  category === "Normal weight"
                                    ? "#185f28"
                                    : category === "Underweight"
                                      ? "rgba(87, 81, 28, 1)"
                                      : category === "Overweight"
                                        ? "#6b5820"
                                        : category === "Obese"
                                          ? "#68232a"
                                          : "#52191f"
                                }
                              />
                              <div>
                                <span className="bmi-result-text">
                                  BMI: {bmi}
                                </span>

                                {/* {ethnicityLabel &&
                                  ethnicityLabel !== "Not Specified" && (
                                    <span className="bmi-ethnicity-text">
                                    Ethnicity:{" "}
                                    {ethnicityLabel.split(" - ")[0]}
                                    </span>
                                    )} */}
                              </div>
                            </div>
                            <span className="bmi-result-badge">{category}</span>
                          </div>
                        </div>
                          <div>

                                    {bmiPlausibility?.tier === "hard_reject" && (
                                      <Alert className="border-red-200 bg-red-50 text-red-900">
                                                    <AlertCircle className="h-4 w-4 text-red-600" />
                                                    <AlertDescription>{bmiPlausibility.message}</AlertDescription>
                                                  </Alert>
                                    )}
    
                                    {bmiPlausibility?.tier === "confirmation_prompt" && !isBmiConfirmedForCurrent && (
                                        <div className="border rounded-lg p-3 border-amber-200 bg-amber-50">
                                                    <div className="flex items-start gap-2 mb-2">
                                                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                                                      <p className="text-sm text-amber-900">{bmiPlausibility.message}</p>
                                                    </div>
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      variant="outline"
                                                      onClick={handleConfirmBmiPlausibility}
                                                      className="border-amber-400 text-amber-900 hover:bg-amber-100"
                                                    >
                                                      Confirm value is correct
                                                    </Button>
                                                  </div>
                                    )}
    
                                    {bmiPlausibility?.tier === "confirmation_prompt" && isBmiConfirmedForCurrent && (
                                      <div className="text-xs px-3 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800">
                                      Unusual value confirmed
                                    </div>
                                    )}
                          </div>
                        </>
                      ) : null;
                    })()}
                  </div>
                )}

                {selectedVital?.label === "Oxygen Saturation" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <label>Oxygen Saturation (%) *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="Value"
                        placeholder="e.g., 98"
                        min="0"
                        max="100"
                        value={values.Value}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.Value && errors.Value && (
                        <p className="field-error">{errors.Value}</p>
                      )}
                    </div>
                  </div>
                )}

                {selectedVital?.label === "NEWS Pain Score" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <label>NEWS Pain Score (0-10) *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        name="Value"
                        placeholder="e.g., 5"
                        min="0"
                        max="10"
                        value={values.Value}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.Value && errors.Value && (
                        <p className="field-error">{errors.Value}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        0 = No pain, 10 = Worst possible pain
                      </p>
                    </div>
                  </div>
                )}

                {selectedVital?.label === "Blood Sugar" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <label>Blood Sugar *</label>
                      <input
                        className="template_input text_input_field"
                        type="number"
                        step="0.1"
                        name="Value"
                        placeholder="e.g., 5.5"
                        value={values.Value}
                        onChange={handleChange}
                        autoFocus
                      />
                      {touched.Value && errors.Value && (
                        <p className="field-error">{errors.Value}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <CustomSelectionDropdown
                        label="Unit *"
                        name="Unit"
                        value={values.Unit}
                        options={[
                          { value: "mmol/L", label: "mmol/L" },
                          { value: "mg/dL", label: "mg/dL" },
                        ]}
                        onChange={(val) =>
                          setFieldValue("Unit", val.target.value)
                        }
                      />
                    </div>
                  </div>
                )}

                {selectedVital?.label === "Consciousness (AVPU)" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <CustomSelectionDropdown
                        label="Consciousness Level *"
                        name="Value"
                        value={values.Value}
                        options={[
                          { value: "Alert", label: "A - Alert" },
                          { value: "Confusion", label: "C - New Confusion" },
                          { value: "Voice", label: "V - Responds to Voice" },
                          { value: "Pain", label: "P - Responds to Pain" },
                          { value: "Unresponsive", label: "U - Unresponsive" },
                        ]}
                        onChange={(val) =>
                          setFieldValue("Value", val.target.value)
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Alert = 0 pts on NEWS2. All others = 3 pts.
                      </p>
                    </div>
                  </div>
                )}

                {selectedVital?.label === "Supplemental Oxygen" && (
                  <div className="fields-wrapper">
                    <div className="form-group">
                      <CustomSelectionDropdown
                        label="On Supplemental Oxygen? *"
                        name="Value"
                        value={values.Value}
                        options={[
                          { value: "No", label: "No" },
                          { value: "Yes", label: "Yes" },
                        ]}
                        onChange={(val) =>
                          setFieldValue("Value", val.target.value)
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        If yes, SpO₂ will use NEWS2 Scale 2 and adds +2 to
                        score.
                      </p>
                    </div>
                  </div>
                )}

                <div className="footer-actions">
                  <button className="cancel" type="button" onClick={closeModal}>
                    Cancel
                  </button>
                  <button
                    className="done"
                    type="submit"
                    disabled={buttonsDisabled || addPatientVitalsLoader || bmiBlocksSave || weightBlocksSave}
                    >
                    {isEditMode
                      ? addPatientVitalsLoader
                        ? `Updating ${selectedVital?.label}...`
                        : `Update ${selectedVital?.label}`
                      : addPatientVitalsLoader
                        ? `Adding ${selectedVital?.label}...`
                        : `Add ${selectedVital?.label}`}
                  </button>
                </div>
              </Form>
            );
          }}
          </Formik>
        )}
      </div>
    </Modal>
  );
}
